import { useMemo } from "react";
import i18next, { type i18n as I18nInstance, type Resource } from "i18next";
import { I18nextProvider, Trans, initReactI18next, useTranslation as useI18nTranslation } from "react-i18next";
import enCommon from "./locales/en";
import arCommon from "./locales/ar";
import { ENUMS, ISSUERS, UNITS, enumCatalogue, type Bilingual, type EnumGroup } from "./enums";

export { I18nextProvider as I18nProvider, Trans };
export * from "./enums";
export { enCommon, arCommon };
export type { CommonCatalog } from "./locales/en";

export type Locale = "en" | "ar";
export const SUPPORTED_LOCALES: readonly Locale[] = ["en", "ar"] as const;
export const STORAGE_KEY = "pmo.locale";

export const isRtl = (locale: string): boolean => locale.startsWith("ar");
export const dirFor = (locale: string): "rtl" | "ltr" => (isRtl(locale) ? "rtl" : "ltr");

/**
 * BCP-47 tags used for every Intl formatter. Arabic keeps Western digits (the convention of federal publications) and the Gregorian
 * calendar; both use the UAE region for currency and separators.
 */
export const INTL_LOCALE: Record<Locale, string> = { en: "en-AE", ar: "ar-AE-u-nu-latn-ca-gregory" };

/**
 * CLDR's Arabic puts the tanween fath before the alif (U+064B U+0627); UAE government Arabic writes it on the alif (يوماً، حالياً).
 * Apply to every Arabic string an Intl formatter returns (relative time, unit and date formats).
 */
export function normalizeArabicIntl(text: string): string {
  return text.replace(/\u064B\u0651\u0627|\u0651\u064B\u0627/g, "\u0651\u0627\u064B").replace(/\u064B\u0627/g, "\u0627\u064B");
}

export interface CreateI18nOptions {
  /** App namespaces merged beside the shared `common` namespace, e.g. `{ en: { backoffice }, ar: { backoffice } }`. */
  resources?: Partial<Record<Locale, Record<string, object>>>;
  defaultLocale?: Locale;
  /** Default namespace for `t()` in the app (the shared `common` stays reachable as `common:key`). */
  defaultNS?: string;
}

function detectLocale(fallback: Locale): Locale {
  if (typeof window === "undefined") return fallback;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "en" || stored === "ar") return stored;
  const nav = window.navigator.language?.toLowerCase() ?? "";
  return nav.startsWith("ar") ? "ar" : fallback;
}

/** Writes `<html lang dir>`; the design system reads both (fonts, RTL variants, internal microcopy). */
export function applyDocumentLocale(locale: Locale): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
  document.documentElement.dir = dirFor(locale);
}

const ARABIC_PLURAL_FORMS = ["zero", "one", "two", "few", "many", "other"] as const;

/**
 * Arabic has six plural forms. A catalogue that spells out only `_one` / `_other` would fall back to English for most counts, so the
 * forms left out take the `_other` text; forms that are spelled out are kept.
 */
function completeArabicPlurals(node: Record<string, unknown>): Record<string, unknown> {
  const complete = (v: unknown): unknown =>
    Array.isArray(v) ? v.map(complete) : v && typeof v === "object" ? completeArabicPlurals(v as Record<string, unknown>) : v;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(node)) out[k] = complete(v);
  for (const k of Object.keys(node)) {
    const base = k.endsWith("_other") ? k.slice(0, -"_other".length) : null;
    if (base === null) continue;
    for (const form of ARABIC_PLURAL_FORMS) if (out[`${base}_${form}`] === undefined) out[`${base}_${form}`] = node[k];
  }
  return out;
}

export function createI18n(options: CreateI18nOptions = {}): I18nInstance {
  const defaultLocale = options.defaultLocale ?? "en";
  const initial = detectLocale(defaultLocale);
  const resources: Resource = {
    en: { common: { ...enCommon, enums: enumCatalogue("en") }, ...(options.resources?.en ?? {}) },
    ar: completeArabicPlurals({ common: { ...arCommon, enums: enumCatalogue("ar") }, ...(options.resources?.ar ?? {}) }) as Resource[string],
  };

  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    resources,
    lng: initial,
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    defaultNS: options.defaultNS ?? "common",
    fallbackNS: "common",
    ns: Object.keys(resources.en ?? {}),
    interpolation: { escapeValue: false },
    returnNull: false,
    initAsync: false,
  });

  applyDocumentLocale(initial);
  instance.on("languageChanged", (lng) => {
    const l: Locale = lng.startsWith("ar") ? "ar" : "en";
    applyDocumentLocale(l);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, l);
  });

  return instance;
}

export const useTranslation = useI18nTranslation;

/** "moderatelyConcentrated" -> "Moderately concentrated" for identifiers without a translation. */
export function humanize(value: string): string {
  const spaced = value.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim().toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export interface LocaleApi {
  locale: Locale;
  dir: "rtl" | "ltr";
  intl: string;
  setLocale: (l: Locale) => void;
  toggle: () => void;
}

/** Current language, its direction and a toggle; switching writes `<html lang dir>` and remembers the choice. */
export function useLocale(): LocaleApi {
  const { i18n } = useI18nTranslation();
  const locale: Locale = i18n.language?.startsWith("ar") ? "ar" : "en";
  return {
    locale,
    dir: dirFor(locale),
    intl: INTL_LOCALE[locale],
    setLocale: (l) => void i18n.changeLanguage(l),
    toggle: () => void i18n.changeLanguage(locale === "ar" ? "en" : "ar"),
  };
}

/** Picks the value for the current language from a bilingual pair (`nameEn` / `nameAr`), falling back to the other language. */
export function pickLocalized(locale: Locale, en: string | null | undefined, ar: string | null | undefined): string {
  return (locale === "ar" ? ar || en : en || ar) ?? "";
}

/** `const L = useBilingual(); L(user.displayNameEn, user.displayNameAr)` or `L({ en, ar })`. */
export function useBilingual(): (en: string | null | undefined | Bilingual, ar?: string | null) => string {
  const { locale } = useLocale();
  return (en, ar) => (typeof en === "object" && en !== null ? en[locale] || en.en : pickLocalized(locale, en, ar));
}

/** Localised label of an enum value; unknown values fall back to a humanised identifier. */
export function enumLabel(locale: Locale, group: EnumGroup, value: string | null | undefined): string {
  if (!value) return "";
  const entry = (ENUMS[group] as Record<string, Bilingual>)[value];
  return entry ? entry[locale] : humanize(value);
}

export function useEnumLabel(): (group: EnumGroup, value: string | null | undefined) => string {
  const { locale } = useLocale();
  return (group, value) => enumLabel(locale, group, value);
}

interface AuditTextSource {
  action: string;
  actionLabelEn?: string | null;
  actionLabelAr?: string | null;
  summary?: string | null;
  summaryAr?: string | null;
}

interface ActorSource {
  actorName: string;
  actorNameAr?: string | null;
}

/** An audit event's action label (falling back to its code), summary, actor and entity type in the current language. */
export function useAuditText() {
  const { locale } = useLocale();
  return useMemo(
    () => ({
      action: (e: AuditTextSource) => pickLocalized(locale, e.actionLabelEn, e.actionLabelAr) || e.action,
      summary: (e: AuditTextSource) => pickLocalized(locale, e.summary, e.summaryAr),
      actor: (e: ActorSource) => pickLocalized(locale, e.actorName, e.actorNameAr),
      entity: (type: string) => enumLabel(locale, "auditEntity", type),
    }),
    [locale],
  );
}

/** Name of a document's issuing authority in the current language; issuers outside the corpus fall back to the record's own Arabic name, then to the text as entered. */
export function useIssuerLabel(): (issuer: string | null | undefined, issuerAr?: string | null) => string {
  const { locale } = useLocale();
  return (issuer, issuerAr) => (issuer ? ISSUERS[issuer]?.[locale] ?? pickLocalized(locale, issuer, issuerAr) : "");
}

/** Units counted in whole things: the English singular for one, and the Arabic noun that agrees with the number before it (the plural from 3 to 10, the accusative singular from 11 to 99, the singular otherwise). */
const COUNTED: Record<string, { en: string; ar: { few: string; many: string; other: string } }> = {
  days: { en: "day", ar: { few: "أيام", many: "يوماً", other: "يوم" } },
  months: { en: "month", ar: { few: "أشهر", many: "شهراً", other: "شهر" } },
  years: { en: "year", ar: { few: "سنوات", many: "سنة", other: "سنة" } },
  hours: { en: "hour", ar: { few: "ساعات", many: "ساعة", other: "ساعة" } },
  persons: { en: "person", ar: { few: "أشخاص", many: "شخصاً", other: "شخص" } },
  sites: { en: "site", ar: { few: "مواقع", many: "موقعاً", other: "موقع" } },
  datasets: { en: "dataset", ar: { few: "مجموعات بيانات", many: "مجموعة بيانات", other: "مجموعة بيانات" } },
  XP: { en: "XP", ar: { few: "نقاط خبرة", many: "نقطة خبرة", other: "نقطة خبرة" } },
};

const pluralRules: Partial<Record<Locale, Intl.PluralRules>> = {};

/** A unit of measure in `locale`, agreeing with `count` when one is given ("1 day", «36 يوماً»); units outside the vocabulary fall back to the record's own Arabic unit, then to the text as entered. */
export function unitText(locale: Locale, unit: string | null | undefined, unitAr?: string | null, count?: number | null): string {
  const key = unit?.trim();
  if (!key) return "";
  const counted = COUNTED[key];
  if (counted && count != null && Number.isFinite(count)) {
    const rule = (pluralRules[locale] ??= new Intl.PluralRules(INTL_LOCALE[locale])).select(count);
    if (locale === "ar") return rule === "few" || rule === "many" ? counted.ar[rule] : counted.ar.other;
    if (rule === "one") return counted.en;
  }
  return UNITS[key]?.[locale] ?? pickLocalized(locale, unit, unitAr);
}

/** A unit of measure in the current language, agreeing with `count` when the number is shown beside it. */
export function useUnitLabel(): (unit: string | null | undefined, unitAr?: string | null, count?: number | null) => string {
  const { locale } = useLocale();
  return useMemo(() => (unit: string | null | undefined, unitAr?: string | null, count?: number | null) => unitText(locale, unit, unitAr, count), [locale]);
}

// ---------------------------------------------------------------- Formatters
export interface Formatters {
  date: (value: string | number | Date | null | undefined, style?: "short" | "medium" | "long") => string;
  dateTime: (value: string | number | Date | null | undefined) => string;
  time: (value: string | number | Date | null | undefined) => string;
  relative: (value: string | number | Date | null | undefined, now?: Date) => string;
  number: (value: number | null | undefined, options?: Intl.NumberFormatOptions) => string;
  compact: (value: number | null | undefined) => string;
  currency: (value: number | null | undefined, options?: { compact?: boolean; currency?: string }) => string;
  percent: (value: number | null | undefined, options?: { fraction?: boolean; digits?: number }) => string;
  days: (value: number) => string;
  /** A Cabinet resolution or law number in the reader's language: "No. (45) of 2024" reads "رقم (45) لسنة 2024". Numbers written another way are shown as entered. */
  instrumentNumber: (value: string | null | undefined) => string;
}

const INSTRUMENT_NUMBER = [/^No\.\s*\((\d+)\)\s*of\s*(\d{4})\s*(\((?:demo|توضيحي|افتراضي|تجريبي)\))?$/i, /^رقم\s*\((\d+)\)\s*لسنة\s*(\d{4})\s*(\((?:demo|توضيحي|افتراضي|تجريبي)\))?$/];

const toDate = (v: string | number | Date): Date => (v instanceof Date ? v : new Date(v));
const valid = (d: Date) => !Number.isNaN(d.getTime());

export function createFormatters(locale: Locale): Formatters {
  const tag = INTL_LOCALE[locale];
  const dateFmt = {
    short: new Intl.DateTimeFormat(tag, { day: "numeric", month: "short" }),
    medium: new Intl.DateTimeFormat(tag, { day: "numeric", month: "short", year: "numeric" }),
    long: new Intl.DateTimeFormat(tag, { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
  };
  const dateTimeFmt = new Intl.DateTimeFormat(tag, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const timeFmt = new Intl.DateTimeFormat(tag, { hour: "2-digit", minute: "2-digit" });
  const rtf = new Intl.RelativeTimeFormat(tag, { numeric: "auto" });
  const compactFmt = new Intl.NumberFormat(tag, { notation: "compact", maximumFractionDigits: 1 });
  const dash = "—";
  /** Arabic Intl output with the tanween on the alif (يوماً). */
  const fix = locale === "ar" ? normalizeArabicIntl : (s: string) => s;
  /** Short dates never break across lines ("30 Sep" / "2026" in a narrow column). */
  const unbroken = (s: string) => s.replace(/ /g, "\u00a0");
  return {
    date: (v, style = "medium") => (v == null || !valid(toDate(v)) ? dash : style === "long" ? fix(dateFmt.long.format(toDate(v))) : unbroken(fix(dateFmt[style].format(toDate(v))))),
    dateTime: (v) => (v == null || !valid(toDate(v)) ? dash : fix(dateTimeFmt.format(toDate(v)))),
    time: (v) => (v == null || !valid(toDate(v)) ? dash : fix(timeFmt.format(toDate(v)))),
    relative: (v, now = new Date()) => {
      if (v == null) return dash;
      const d = toDate(v);
      if (!valid(d)) return dash;
      const seconds = Math.round((d.getTime() - now.getTime()) / 1000);
      const abs = Math.abs(seconds);
      if (abs < 45) return fix(rtf.format(0, "second"));
      if (abs < 3600) return fix(rtf.format(Math.round(seconds / 60), "minute"));
      if (abs < 86400) return fix(rtf.format(Math.round(seconds / 3600), "hour"));
      if (abs < 86400 * 30) return fix(rtf.format(Math.round(seconds / 86400), "day"));
      if (abs < 86400 * 365) return fix(rtf.format(Math.round(seconds / (86400 * 30)), "month"));
      return fix(rtf.format(Math.round(seconds / (86400 * 365)), "year"));
    },
    number: (v, options) => (v == null || Number.isNaN(v) ? dash : fix(new Intl.NumberFormat(tag, options).format(v))),
    compact: (v) => (v == null || Number.isNaN(v) ? dash : compactFmt.format(v)),
    currency: (v, options) => {
      if (v == null || Number.isNaN(v)) return dash;
      const currency = options?.currency ?? "AED";
      const notation = options?.compact ? "compact" : "standard";
      const maximumFractionDigits = options?.compact ? 1 : 0;
      // UAE government Arabic writes dirham amounts as «50,000 درهم» / «2.5 مليون درهم», not with the «د.إ.» symbol.
      if (locale === "ar" && currency === "AED") return fix(`${new Intl.NumberFormat(tag, { notation, maximumFractionDigits }).format(v)} درهم`);
      return new Intl.NumberFormat(tag, { style: "currency", currency, notation, maximumFractionDigits }).format(v);
    },
    percent: (v, options) =>
      v == null || Number.isNaN(v) ? dash : new Intl.NumberFormat(tag, { style: "percent", maximumFractionDigits: options?.digits ?? 0 }).format(options?.fraction ? v : v / 100),
    days: (v) => fix(new Intl.NumberFormat(tag, { style: "unit", unit: "day", unitDisplay: "long" }).format(v)),
    instrumentNumber: (v) => {
      if (!v?.trim()) return dash;
      const m = INSTRUMENT_NUMBER.map((r) => r.exec(v.trim())).find(Boolean);
      if (!m) return v;
      const [, n, year, demo] = m;
      return locale === "ar" ? `رقم (${n}) لسنة ${year}${demo ? " (توضيحي)" : ""}` : `No. (${n}) of ${year}${demo ? " (demo)" : ""}`;
    },
  };
}

/** Locale-aware formatters (en-AE / ar-AE): dates, relative time, numbers, AED currency and percentages. */
export function useFormat(): Formatters {
  const { locale } = useLocale();
  return useMemo(() => createFormatters(locale), [locale]);
}
